// GalleryPageBase.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FilterBar, { FilterOption } from './FilterBar';
import '../pages/image-view/GalleryPage.css';
import Loading from './Loading';
import { ImagePreviewMeta, ImageProps } from './ImagePreview';
import CardGallery from './CardGallery';
import SelectionToolbar from './SelectionToolBar';
import { Modal, notification, message } from 'antd';
import DatasetCreationModal from './DatasetCreation';


// Define interfaces for better type safety
export interface FilterOptions {
  models: string[];
  part_names: string[];
  part_numbers: string[];
  cameras: string[];
  dispositions: string[];
  systems: string[];
}

interface ActionButton {
  text: string;
  onClick: () => void;
}

export interface GalleryConfig {
  imagesEndpoint: string;
  filterOptionsEndpoint: string;
  cardClickPath: string;
  pageTitle?: string;
  customFilterOptions?: FilterOption[];
  pageType: 'image-view' | 'dataset-view';
  datasetId?: number;
  monolithic?: boolean;
}

// Define a cache duration (e.g., 10 minutes)
const CACHE_DURATION_MS = 10 * 60 * 1000;

interface FilterCache {
  timestamp: number;
  data: FilterOptions;
}

const GalleryPageBase: React.FC<GalleryConfig> = ({
  imagesEndpoint,
  filterOptionsEndpoint,
  cardClickPath,
  pageTitle = 'Gallery',
  customFilterOptions,
  pageType,
  datasetId,
  monolithic = false
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [images, setImages] = useState<ImageProps[]>([]);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('limit') || '20', 10));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(true);
  const [systemsLoaded, setSystemsLoaded] = useState(false);
  // Add to your GalleryPageBase component's state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Array<{id: string, system_name: string}>>([]);
  const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [sortBy, setSortBy]       = useState<string>(searchParams.get('sort_by') || 'timestamp');
  const [sortOrder, setSortOrder] = useState<'asc'|'desc'>( (searchParams.get('sort_order') as any) || 'desc' );

  // Add a toggle function for selection mode
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      // Clear selections when exiting selection mode
      setSelectedImages([]);
    }
  };

  const fetchSystemsOnly = async () => {
    setFilterLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${filterOptionsEndpoint}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch systems');
      }
      
      const data = await response.json();
      setFilterOptions(data);
      setSystemsLoaded(true);
    } catch (error) {
      console.error('Error fetching systems:', error);
    } finally {
      setFilterLoading(false);
    }
  };


  // Add a function to handle image selection
  const handleImageSelection = (imageId: string, systemName: string) => {
    // Convert ID to string for consistent comparison
    const idStr = imageId.toString();
    
    // console.log('Toggling selection for image:', idStr, 'in system:', systemName);
    
    setSelectedImages(prev => {
      // Check if this image is already selected
      const isSelected = prev.some(item => item.id === idStr && item.system_name === systemName);
      
      if (isSelected) {
        // If already selected, remove it
        return prev.filter(item => !(item.id === idStr && item.system_name === systemName));
      } else {
        // Otherwise add it to selection
        return [...prev, { id: idStr, system_name: systemName }];
      }
    });
  };

  // Add a function to handle bulk selection
  const handleSelectAll = () => {
    const allSelected = images.length > 0 && 
      images.every(img => 
        selectedImages.some(item => 
          item.id === img.id.toString() && item.system_name === img.system_name
        )
      );
    
    if (allSelected) {
      // If all are selected, deselect all
      setSelectedImages([]);
    } else {
      // Select all visible images
      const allImageData = images.map(img => ({
        id: img.id.toString(),
        system_name: img.system_name
      }));
      setSelectedImages(allImageData);
    }
  };

  // Handle dataset creation
  const handleCreateDataset = () => {
    if (selectedImages.length > 0) {
      setIsDatasetModalOpen(true);
    }
  };

  const submitDatasetAddition = async (dataset_id: string) => {
    // console.log('Adding selected images to dataset:', dataset_id);

    const imageData = selectedImages.map(item => ({
      id: parseInt(item.id, 10), // Convert string ID back to number
      system_name: item.system_name
    }));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/datasets`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_data: imageData,
          dataset_id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add images to dataset');
      }

      const datasetResponse = await response.json();

      // Reset after successful addition
      setSelectionMode(false);
      setSelectedImages([]);
      setIsDatasetModalOpen(false);

      return datasetResponse;
    } catch (error) {
      console.error('Error adding images to dataset:', error);
    }
  };

  const handleRemoveFromDataset = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/datasets/${datasetId}/images`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image_ids: selectedImages.map(item => item.id) })
      });

      if (!response.ok) {
        throw new Error('Failed to remove images from dataset');
      }

      notification.success({ message: 'Images removed successfully' });
      setSelectedImages([]);
      fetchGallery(1, pageSize, filters);
    } catch (error) {
      notification.error({ message: 'Failed to remove images', description: (error as Error).message });
    } finally {
      setIsRemoveModalOpen(false);
    }
  };

  // Submit dataset creation to API
  const submitDatasetCreation = async (name: string, description: string) => {
    try {
      // Extract the data in the format expected by your API
      const imageData = selectedImages.map(item => ({
        id: parseInt(item.id, 10), // Convert string ID back to number
        system_name: item.system_name
      }));

      // console.log('Creating dataset with image data:', imageData);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/datasets`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          description,
          image_data: imageData, // Send the paired image ID and system name
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create dataset');
      }
      const newDataset = await response.json();
      
      // Reset after successful creation
      setSelectionMode(false);
      setSelectedImages([]);
      setIsDatasetModalOpen(false);
      setIsDatasetModalOpen(false);
      
      // You might want to show a success notification here
      return newDataset;
    } catch (error) {
      console.error('Error creating dataset:', error);
      // Handle error - show toast/notification
    }
  };
  // Check if all visible images are selected
  const areAllSelected = images.length > 0 && images.every(img => 
    selectedImages.some(item => 
      item.id === img.id.toString() && item.system_name === img.system_name
    )
  );
  // State for filter options from the backend
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    models: [],
    part_names: [],
    part_numbers: [],
    cameras: [],
    dispositions: [],
    systems: []
  });

  const getSingleParamFromUrl = (param: string): string | undefined => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param) || undefined;
  };

  // Update filters state so that select filters use arrays for multiple values
  const [filters, setFilters] = useState({
    part_name: getArrayParamFromUrl('part_name'),
    part_number: getArrayParamFromUrl('part_number'),
    model: getArrayParamFromUrl('model'),
    camera: getArrayParamFromUrl('camera'),
    disposition: getArrayParamFromUrl('disposition'),
    system_name: getSingleParamFromUrl('system_name'),
    time_range: getArrayParamFromUrl('time_range'),
  });
  // console.log('Initial filters:', filters);
  const navigate = useNavigate();

  // Helper function to get array parameters from URL
  function getArrayParamFromUrl(paramName: string): string[] {
    const params = searchParams.getAll(paramName);
    return params.length > 0 ? params : [];
  }
  
  // Initial load effect
  useEffect(() => {
    // Check if we have a system_name in URL/initial state
    if (!monolithic) {
      const cacheKey = `filterOptionsCache_${filterOptionsEndpoint}`;
      const cachedFilters = getFilterOptionsFromCache(cacheKey);
      
      if (cachedFilters) {
        setFilterOptions(cachedFilters);
        setSystemsLoaded(true);
        // Load gallery with the initial filters
        fetchGallery(currentPage, pageSize, filters);
      } else {
        // Fetch filter options for the selected system
        fetchFilterOptionsGlobal().then(() => {
          // Load gallery after filter options are loaded
          fetchGallery(currentPage, pageSize, filters);
        });
      }
    }
    const initialSystemName = filters.system_name;
    console.log('Initial system_name from URL:', initialSystemName);
    if (initialSystemName) {
      // If we have a system selected, try to load from cache first
      const cacheKey = `filterOptionsCache_${filterOptionsEndpoint}_${initialSystemName}`;
      const cachedFilters = getFilterOptionsFromCache(cacheKey);
      
      if (cachedFilters) {
        console.log('Loaded filter options from cache for system:', initialSystemName);
        setFilterOptions(cachedFilters);
        setSystemsLoaded(true);
        // Load gallery with the initial filters
        fetchGallery(currentPage, pageSize, filters);
        setFilterLoading(false);
      } else {
        console.log('Fetching filter options for system:', initialSystemName);
        // Fetch filter options for the selected system
        fetchFilterOptionsForSystem(initialSystemName).then(() => {
          // Load gallery after filter options are loaded
          fetchGallery(currentPage, pageSize, filters);
        });
      }
    } else {
      // No system selected, just fetch available systems
      fetchSystemsOnly();
    }
  }, []); // Only run once on mount

  useEffect(() => {
    if (currentPage !== 1) {
      fetchGallery(currentPage, pageSize, filters);
    }
  }, [currentPage]);

  const getFilterOptionsFromCache = (key: string) => {
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Cache for 1 hour
        if (Date.now() - timestamp < 3600000) {
          return data;
        }
      }
    } catch (e) {
      console.error('Cache read error:', e);
    }
    return null;
  };

  const saveFilterOptionsToCache = (key: string, data: any) => {
    try {
      localStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('Cache write error:', e);
    }
  };

  const fetchFilterOptionsGlobal = async () => {
    setFilterLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${filterOptionsEndpoint}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch filter options');
      }
      
      const data = await response.json();
      setFilterOptions(data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setFilterLoading(false);
    }
  };

  const fetchFilterOptionsForSystem = async (systemName: string) => {
  // Check cache first
  const cacheKey = `filterOptionsCache_${filterOptionsEndpoint}_${systemName}`;
  const cachedFilters = getFilterOptionsFromCache(cacheKey);
  
  if (cachedFilters) {
    setFilterOptions(cachedFilters);
    setFilterLoading(false);
    return; // Exit early if we have cached data
  }
  
  // If not in cache, fetch from API
  setFilterLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${filterOptionsEndpoint}?system_name=${encodeURIComponent(systemName)}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch filter options');
      }
      
      const data = await response.json();
      setFilterOptions(data);
      
      // Save to cache for next time
      saveFilterOptionsToCache(cacheKey, data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setFilterLoading(false);
    }
  };
  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    
    // If system_name changes, fetch new filter options
    if (name === 'system_name') {
      if (value) {
        // Create the new filters object
        const newFilters = {
          part_name: [],
          part_number: [],
          model: [],
          camera: [],
          disposition: [],
          system_name: value,
          time_range: filters.time_range, // Keep time range
        };
        
        // Update state
        setFilters(newFilters);
        fetchFilterOptionsForSystem(value);
        
        // Pass the new filters directly instead of relying on state
        fetchGallery(1, pageSize, newFilters);
        setCurrentPage(1);
      } else {
        // If system is cleared, reset to only systems
        setFilterOptions({
          models: [],
          part_names: [],
          part_numbers: [],
          cameras: [],
          dispositions: [],
          systems: filterOptions.systems, // Keep available systems
        });
      }
    }
  };

  const handleApplyFilters = () => {
  // Don't apply filters if no system is selected
  if (!filters.system_name) {
    message.warning('Please select a system first');
    return;
  }
  fetchGallery(1, pageSize, filters); // Reset to page 1 when applying filters
  setCurrentPage(1);
};

  const updateUrlWithFilters = (page: number, limit: number, filterValues: Record<string, any>) => {
    const newParams = new URLSearchParams();
    
    // Add pagination params
    newParams.set('page', page.toString());
    newParams.set('limit', limit.toString());
    newParams.set('sort_by', sortBy);
    newParams.set('sort_order', sortOrder);
    // Add filter params
    Object.entries(filterValues).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Clear previous values before adding new ones
        if (value.length > 0) {
          value.forEach(val => newParams.append(key, val));
        }
      } else if (value) {
        newParams.set(key, value);
      }
    });
    
    // Update the URL without reloading the page
    setSearchParams(newParams);
  };

  const fetchGallery = async (page: number, size: number, filterValues: Record<string, any>) => {
    setLoading(true);
    // console.log('Fetching new gallery at page', page, 'with filters:', filterValues);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: size.toString(),
      });
      queryParams.append('sort_by', sortBy);
      queryParams.append('sort_order', sortOrder);
      // Append each filter to query parameters
      Object.entries(filterValues).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            // Append each value separately (e.g., ?model=ModelA&model=ModelB)
            value.forEach(val => queryParams.append(key, val));
          }
        } else if (value) {
          queryParams.append(key, value);
        }
      });
      updateUrlWithFilters(page, size, filterValues);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${imagesEndpoint}?${queryParams.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch gallery data');
      }
      
      const data = await response.json();
      setImages(data.images);
      setTotalPages(data.total_pages);
      setTotalItems(data.total_count);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  // Define default filter options for the FilterBar component
  const defaultFilterOptions: FilterOption[] = [
    {
      name: "part_name",
      label: "Part Name",
      type: "select",
      options: filterOptions.part_names,
      disabled: !filters.system_name
    },
    {
      name: "part_number",
      label: "Part Number",
      type: "select",
      disabled: !filters.system_name ,
      options: filterOptions.part_numbers,
      
    },
    {
      name: "model",
      label: "Model",
      type: "select",
      disabled: !filters.system_name,
      options: filterOptions.models,
    },
    {
      name: "camera",
      label: "Camera",
      type: "select",
      disabled: !filters.system_name ,
      options: filterOptions.cameras,
    },
    {
      name: "disposition",
      label: "Disposition",
      type: "select",
      disabled: !filters.system_name,
      options: filterOptions.dispositions,
    },
    {
      name: "system_name",
      label: "System Name",
      multiple: false,
      type: "select",
      options: filterOptions.systems,
    },
    {
      name: "time_range",
      label: "Time Range",
      disabled: !filters.system_name,
      type: "timerange",
    },
  ];

  // Use custom filter options if provided, otherwise use default
  const filterBarOptions = customFilterOptions || defaultFilterOptions;
  // Modify the return section of your GalleryPageBase component to include selection UI
  return (
    <div className="gallery-container">
      {loading && <Loading message={`Loading ${pageTitle}...`} />}
      
      <FilterBar
        filters={filters}
        filterOptions={filterBarOptions}
        onFilterChange={handleFilterChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={(newSortBy, newSortOrder) => {
          setSortBy(newSortBy);
          setSortOrder(newSortOrder);
          // fetchGallery(currentPage, pageSize, filters);
        }}
        onApplyFilters={handleApplyFilters}
        isLoading={filterLoading}
      />
      
      {/* Add Selection Toolbar */}
      <SelectionToolbar
        selectionMode={selectionMode}
        toggleSelectionMode={toggleSelectionMode}
        selectedCount={selectedImages.length}
        totalCount={images.length}
        onSelectAll={handleSelectAll}
        actionButton={{
          text: pageType === 'image-view' ? 'Add to Dataset' : 'Remove from Dataset',
          onClick: pageType === 'image-view' ? handleCreateDataset : () => setIsRemoveModalOpen(true),
        }}
        allSelected={areAllSelected}
      />

      <CardGallery
        CardComponent={ImagePreviewMeta}
        cardClickPath={cardClickPath}
        data={images}
        totalPages={totalPages}
        currentPage={currentPage}
        loading={loading}
        onPageChange={(page: number, size?: number) => {
          setCurrentPage(page);
          if (size && size !== pageSize) {
            setPageSize(size);
            fetchGallery(1, size, filters);
          } else {
            fetchGallery(page, pageSize, filters);
          }
        }}
        itemsPerPage={pageSize}
        totalItems={totalItems}
        // Add selection props
        selectionMode={selectionMode}
        selectedItems={selectedImages}
        onItemSelect={handleImageSelection}
      />
      
      {/* Dataset Creation Modal */}
      <DatasetCreationModal
        isOpen={isDatasetModalOpen}
        onClose={() => setIsDatasetModalOpen(false)}
        selectedCount={selectedImages.length}
        onCreateDataset={submitDatasetCreation}
        selectedImages={selectedImages}
        onAddToDataset={submitDatasetAddition}
      />

      <Modal
        title="Remove Images"
        visible={isRemoveModalOpen}
        onOk={handleRemoveFromDataset}
        onCancel={() => setIsRemoveModalOpen(false)}
        okText="Remove"
        cancelText="Cancel"
      >
        <p>Are you sure you want to remove {selectedImages.length} images from this dataset?</p>
      </Modal>
    </div>
  );
};

export default GalleryPageBase;